// ── #410, Session 78n8 — has the vendor actually confirmed the order? ─────────
// Extracted from PurchasingQueuePage.js at Session 79.14c.
//
// IN PLAIN TERMS: when Jennifer places an order she has no number from the
// vendor yet — the vendor issues its own reference when it acknowledges, hours
// or days later. Until then nobody knows whether the order was even received.
// An order sitting unacknowledged for a week is the one worth chasing, and
// before this there was nothing anywhere in Stede that could say so.
//
// WHY THIS NEEDS NO NEW COLUMN, which is the whole reason it is cheap:
// `vendor_order_ref` already exists and already holds the vendor's number. So
// "awaiting confirmation" is DERIVED — an order that is placed and has no
// vendor number yet — rather than a second flag that could disagree with it.
//
// COMPETITIVE NOTE, researched before this was designed. SAP carries the same
// two ideas on a purchase order item: EKPO-LABNR is the supplier's own
// acknowledgment number, and EKPO-KZABS is a flag saying an acknowledgment is
// REQUIRED, which drives a chasing workflow against expected-versus-received.
// Stede matches the first exactly and gets the second's OUTCOME without its
// cost, because the derived rule needs no flag to be set correctly by hand.
// The case SAP's flag exists for is a supplier who never acknowledges at all;
// if a D&R vendor turns out to work that way, the honest fix is a per-vendor
// exception, not a per-order checkbox nobody remembers to tick.
//
// THRESHOLDS ARE DELIBERATELY AgeBadge's OWN — 3 days amber, 7 days red.
// Colby's argument for building this at all was that the queue already fires
// colour-coded ageing warnings and a confirmation warning would "match the
// interface's build naturally." Inventing a second set of thresholds here
// would break the very consistency that made it worth building.
// Measured from `ordered_at`, NOT `created_at`. The clock that matters starts
// when the order left, not when somebody asked for the thing — a request that
// sat in the queue for a fortnight before being ordered has not kept a vendor
// waiting for a fortnight, and colouring it red would be a lie about whose
// court the ball is in.

import React from 'react';
import { ageDays, isAwaitingConfirmation } from '../../lib/purchasingQueue';

export default function ConfirmationBadge({ req }) {
  if (!isAwaitingConfirmation(req)) return null;
  const days = ageDays(req.ordered_at);
  const color = days >= 7 ? '#A32D2D' : days >= 3 ? '#9A5F00' : '#7A7A7A';
  const bg = days >= 7 ? '#FFEBEE' : days >= 3 ? '#FAEEDA' : '#F2F2F0';
  const border = days >= 7 ? '#FFCDD2' : days >= 3 ? '#EF9F27' : '#E0E0E0';
  return (
    <span
      title="The vendor has not sent an order confirmation yet"
      style={{
        fontSize: 10, fontWeight: 700, color, background: bg,
        border: `1px solid ${border}`, borderRadius: 4, padding: '1px 6px',
        flexShrink: 0, whiteSpace: 'nowrap',
      }}
    >
      Unconfirmed{days > 0 ? ` · ${days}d` : ''}
    </span>
  );
}
